import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { unlockUser } from "service/userservice/unlockUser";
import { UnlockUserConfirmationModel } from "components/ModalDialogs/UnlockUserConfirmationModel";
import { deactivateUser as deactivateSelectedUser } from "service/userservice/deactivateUser";
import { removeUser } from "redux/slices/userSlice";
import { useTranslation } from "react-i18next";

import config from "config/config";
import GeneralActionButtonsModal from "components/General/GeneralActionButtonsModal";
import ProfileCard from "../components/Profile/ProfileCard";
import SimpleCTAToDialogCard from "../components/General/SimpleCTAToDialogCard";
import UserInfoDialog from "components/Profile/Dialogs/UserInfoDialog";
import UserSecurityDialog from "components/Profile/Dialogs/UserSecurityDialog";
import UserNotificationDialog from "components/Profile/Dialogs/UserNotificationDialog";
import VariableContractDialog from "components/Profile/Dialogs/VariableContractDialog";

export default function Profile() {
  let navigate = useNavigate();
  let dispatch = useDispatch();
  const { t } = useTranslation();
  let [searchParams, setSearchParams] = useSearchParams();
  let [openUserInfoDialog, setOpenUserInfoDialog] = useState(false);
  let [openUserSecurityDialog, setOpenUserSecurityDialog] = useState(false);
  let [openUserNotificationDialog, setOpenUserNotificationDialog] =
    useState(false);
  const { user } = useSelector((s) => s.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { policies } = useSelector((state) => state.data);
  const [isUnlockUserConfirmation, setIsUnlockUserConfirmation] =
    useState(false);
  const [deactivateUser, setDeactivateUser] = useState(false);
  const [openVariableContractDialog, setOpenVariableContractDialog] =
    useState(false);

  useEffect(() => {
    let openDialog = searchParams?.get("opendialog");
    if (openDialog?.toLowerCase() === "userinfo") {
      setOpenUserInfoDialog(true);
    }
  }, [searchParams]);

  useBreadcrumb([
    {
      name: t("ProfilePage-home"),
      url: "/policies",
    },
    { name: t("ProfilePage-profile"), url: "#" },
  ]);

  async function unlock() {
    // portal api call for unlock user
    console.log("Unlock the account", user);
    const userUnlockResponse = await unlockUser(user?.userId).catch((error) => {
      console.log(error);
    });
    if (userUnlockResponse?.ok) {
      setIsUnlockUserConfirmation(true);
    } else {
      console.log(t("CustomerProfile-unlockUserError"));
    }
  }

  const hasVariableContract = () => {
    return policies?.some((policy) =>
      ["LQ", "LZ", "AVD"].includes(policy?.product?.type)
    );
  };

  async function deactivate() {
    // portal api call for deactivate user
    await deactivateSelectedUser(user?.userId)
      .then((response) => {
        if (response) {
          dispatch(removeUser());
          navigate("/policies");
        }
      })
      .catch((error) => {
        console.log(error);
      });
    setDeactivateUser(false);
  }

  return (
    <>
      {/* SPRINT-9-AGENT-help-sales-and-search.pdf (pg. 17) */}
      <div className="container mb-8">
        <h1>{t("CustomerProfile-title")}</h1>
        <div className="flex justify-between">
          <p>{t("CustomerProfile-subTitle")}</p>
          {csrUser?.userId && (
            <>
              <div className="flex justify-end">
                <button className="ui-btn-primary" onClick={() => unlock()}>
                  {t("CustomerProfile-button1")}
                </button>
                <button
                  className="ui-btn-primary"
                  onClick={() => setDeactivateUser(true)}
                >
                  {t("CustomerProfile-button2")}
                </button>
              </div>
            </>
          )}
        </div>
        <hr />
      </div>

      {/* Profile Details */}
      <ProfileCard />

      {/* Manage my information & Preferences */}
      <div className="container ui-component-spacing">
        <h2>{t("CustomerProfile-title2")}</h2>
        <p className="text-sm">{t("CustomerProfile-subTitle2")}</p>
        <hr />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          <SimpleCTAToDialogCard
            title={t("CustomerProfile-cardTitle1")}
            desc={t("CustomerProfile-cardDescription1")}
            ctaText={t("CustomerProfile-cardButton1")}
            openDialog={() => {
              if (hasVariableContract()) {
                setOpenVariableContractDialog(true);
              } else {
                setOpenUserInfoDialog(true);
              }
            }}
          />
          {/* Contact Information */}
          <UserInfoDialog
            dialogState={openUserInfoDialog}
            setDialogState={setOpenUserInfoDialog}
          />

          <SimpleCTAToDialogCard
            title={t("CustomerProfile-cardTitle2")}
            desc={t("CustomerProfile-cardDescription2")}
            ctaText={t("CustomerProfile-cardButton2")}
            openDialog={() => setOpenUserSecurityDialog(true)}
          />
          {/* Security Settings */}
          <UserSecurityDialog
            dialogState={openUserSecurityDialog}
            setDialogState={setOpenUserSecurityDialog}
          />
          {config.E_DELIVERY_ENABLE === "true" && (
            <>
              <SimpleCTAToDialogCard
                title={t("CustomerProfile-cardTitle3")}
                desc={t("CustomerProfile-cardDescription3")}
                ctaText={t("CustomerProfile-cardButton2")}
                openDialog={() => setOpenUserNotificationDialog(true)}
              />
              {/*  Manage Notification Preferences */}
              <UserNotificationDialog
                showDialog={openUserNotificationDialog}
                setShowDialog={setOpenUserNotificationDialog}
              />
            </>
          )}
          <UnlockUserConfirmationModel
            isUnlockUserConfirmation={isUnlockUserConfirmation}
            setIsUnlockUserConfirmation={setIsUnlockUserConfirmation}
          />
        </div>
      </div>
      <VariableContractDialog
        showDialog={openVariableContractDialog}
        setShowDialog={setOpenVariableContractDialog}
      />
      <GeneralActionButtonsModal
        title={t("CustomerProfile-modalTitle")}
        primaryButtonText={t("CustomerProfile-modalButton1")}
        secondaryButtonText={t("CustomerProfile-modalButton2")}
        dialogState={deactivateUser}
        setDialogState={setDeactivateUser}
        primaryButtonAction={deactivate}
      />
      {/* supressed for phase 1 */}
      {/* Other Settings */}
      {/* <div className="container ui-component-spacing">
        <h2>Other settings</h2>
        <p className="text-sm">
          Subtitle explaining this group of functions goes here
        </p>
        <hr />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
          <AppNavigationCard
            icon="#ui-icon-profile"
            title="Manage Beneficiary(s)"
            desc="Here goes a brief description"
            url="/policies/beneficiaries/5723452"
          />
          <AppNavigationCard
            icon="#ui-icon-payment"
            title="Payment Settings"
            desc="Here goes a brief description"
            url="/payment/manage-payments"
          />
          <AppNavigationCard
            icon="#ui-icon-heart"
            title="Manage Favorites"
            desc="Here goes a brief description"
            url="/"
          />
          <AppNavigationCard
            icon="#ui-icon-help"
            title="Another Thing You Can Do"
            desc="Here goes a brief description"
            url="/"
          />
        </div>
      </div> */}
    </>
  );
}
